import Api from "@/services/Api";
import { useParams } from "react-router-dom";
import { useMessenger } from "@/framework/Messenger/Messenger";
import type { FieldProps } from "@rjsf/utils";
import { type FC, useEffect, useState } from "react";
import { Alert, Box, Card, CardContent, IconButton, styled, Typography } from "@mui/material";
import type { KulturioUserPortalPollResponseLog } from "@/declarations/models/KulturioUserPortal";
import PollResponseResultsTable from "@/framework/KioForm/fields/PollResponseResultsField/PollResponseResultsTable";
import PollResponseTimeSeriesPlot from "@/framework/KioForm/fields/PollResponseResultsField/PollResponseTimeSeriesPlot";
import { getLanguageCode, resolvePotentiallyLocalizedString } from "@/utils/obj";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { Refresh } from "@mui/icons-material";

interface PollResponseResultsFieldProps extends Partial<FieldProps> {}

const Container = styled("div")`
  padding-top: 30px;
  margin-left: 16px;
  margin-right: 16px;
`;

const InfoBoxContainer = styled(Box)`
  display: flex;
  gap: 32px;
`;

export interface ModifiedPollResponse {
  poll_data: Record<string, number>;
  poll_response_logs?: KulturioUserPortalPollResponseLog[];
  total: number;
  weekTotal: number;
}

interface PollResponseInfoCardProps {
  infoTitle: string;
  infoValue: string | number;
}

const getLastWeekCount = (pollResponseLog: KulturioUserPortalPollResponseLog[] | undefined) => {
  if (!pollResponseLog) return 0;
  const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  let count = 0;
  for (let i = 0; i < pollResponseLog.length; i++) {
    const logDate = new Date(pollResponseLog[i].logged_at).getTime();
    if (logDate >= oneWeekAgo) {
      count++;
    }
  }
  return count;
};

const PollResponseInfoCard: FC<PollResponseInfoCardProps> = ({ infoTitle, infoValue }) => {
  return (
    <Card variant={"outlined"} sx={{ mb: "32px", pr: "32px" }}>
      <CardContent>
        <Typography sx={{ fontWeight: 600, fontSize: "16px" }}>{infoTitle}</Typography>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "32px",
          }}
        >
          {infoValue}
        </Typography>
      </CardContent>
    </Card>
  );
};

const PollResponseResultsField: FC<PollResponseResultsFieldProps> = ({ formContext, ...props }) => {
  const currentDocumentId = formContext.currentDocumentId;
  const { t } = useTranslation("common");
  const msg = useMessenger();
  const getLocalizedString = resolvePotentiallyLocalizedString(getLanguageCode(i18next.language));
  const [pollResponse, setPollResponse] = useState<ModifiedPollResponse | null>(null);
  const idSchemaIdOfParentTemplate = "kio-form-root";
  const answerIds = formContext?.sharedState?.[idSchemaIdOfParentTemplate]?.answersIds ?? {};

  const getPollResponse = async (documentId: number, noCache: boolean = false) => {
    const apirequest = noCache
      ? Api.getOneKulturioUserPortalPollResponseNoCache(documentId, { show_logs: true })
      : Api.getOneKulturioUserPortalPollResponse(documentId, { show_logs: true });

    apirequest
      .fetch()
      .then((response) => {
        const [res, err, _source] = response;
        if (err) {
          throw err;
        }
        if (res?.poll_data) {
          const newPollData: Record<string, number> = {};
          let total = 0;

          Object.entries(res?.poll_data).forEach(([pollKey, pollKeyValue]) => {
            if (Object.keys(answerIds).length > 0) {
              newPollData[getLocalizedString(answerIds[pollKey])] = pollKeyValue;
            }
            total += pollKeyValue;
          });
          const weekTotal = getLastWeekCount(res?.poll_response_logs);

          const modifiedPollResponse: ModifiedPollResponse = {
            poll_data: Object.keys(newPollData).length > 0 ? newPollData : res.poll_data,
            total: total,
            poll_response_logs: res.poll_response_logs,
            weekTotal: weekTotal,
          };

          setPollResponse(modifiedPollResponse);
        }
      })
      .catch((err) => {
        if (err?.code) {
          msg.error(`code: ${err.code}, message: ${err.message}`);
        } else {
          msg.error(err);
        }
      });
  };

  useEffect(() => {
    if (!currentDocumentId) {
      return;
    }

    getPollResponse(currentDocumentId).catch((err) => {
      console.error(err);
    });
  }, [currentDocumentId]);

  return (
    <Container>
      {!!pollResponse ? (
        <>
          <InfoBoxContainer>
            <PollResponseInfoCard infoTitle={t("polls.totalPollSubmits")} infoValue={pollResponse.total} />
            <PollResponseInfoCard infoTitle={t("polls.submitsLastSevenDays")} infoValue={pollResponse.weekTotal} />
          </InfoBoxContainer>

          <PollResponseResultsTable pollResults={pollResponse} />

          <PollResponseTimeSeriesPlot responseLog={pollResponse?.poll_response_logs?.map((el) => el?.logged_at)} />
        </>
      ) : (
        <Alert severity="warning">
          <strong>{t("polls.noPollsFoundTitle")}</strong>
          {!currentDocumentId ? (
            <p>{t("polls.noPollsFoundDescriptionNoDocumentId")}</p>
          ) : (
            <>
              <p>{t("polls.noPollsFoundDescriptionP1")}</p>
              <p>{t("polls.noPollsFoundDescriptionP2")}</p>
            </>
          )}
        </Alert>
      )}
      <IconButton
        disabled={!currentDocumentId}
        onClick={async () => {
          await getPollResponse(currentDocumentId, true);
        }}
      >
        <Refresh />
      </IconButton>
    </Container>
  );
};
export default PollResponseResultsField;
